import { useActorRef } from "@xstate/react";
import { CommandInput } from "cmdk";
import { X } from "lucide-react";
import {
  memo,
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import { VStack } from "@/components/Layout";
import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/cn";

import {
  type SelectedEquipmentOption,
  createEquipmentSelectMachine,
} from "../-xstate/equipment-select-machine";
import type { Attribute, FieldRenderInputProps } from "../../types";

import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";
import { ReadOnlyField } from "./ReadOnlyField";

type Props = BaseFieldProps & {
  equipmentType: "all" | "providable";
};

/**
 * 備品・消耗品(貸出可能)を検索するフィールドコンポーネント
 */
export const EquipmentSelectField = memo((props: BaseFieldProps) => {
  return <_EquipmentSelectField {...props} equipmentType="all" />;
});
EquipmentSelectField.displayName = "EquipmentSelectField";

/**
 * 備品・消耗品(提供可能)を検索するフィールドコンポーネント
 */
export const EquipmentProvidableSelectField = memo((props: BaseFieldProps) => {
  return <_EquipmentSelectField {...props} equipmentType="providable" />;
});
EquipmentProvidableSelectField.displayName = "EquipmentProvidableSelectField";

const _EquipmentSelectField = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    layout,
    className,
    "data-testid": testId,
    equipmentType,
  }: Props) => {
    const renderInput = useCallback(
      ({
        value: inputValue,
        onChange: handleChange,
        placeholder,
        disabled: isDisabled,
      }: FieldRenderInputProps) => {
        const machine = useMemo(
          () => createEquipmentSelectMachine(equipmentType),
          // oxlint-disable-next-line react-hooks/exhaustive-deps
          [equipmentType],
        );
        const stateMachineService = useActorRef(machine, {
          input: {
            selectedEquipmentId: inputValue as string,
          },
        });

        stateMachineService.send({ type: "INIT" });
        // // useSyncExternalStoreを使用して状態を一括取得
        const state = useSyncExternalStore(
          (callback) => {
            const subscription = stateMachineService.subscribe(callback);
            return () => subscription.unsubscribe();
          },
          () => stateMachineService.getSnapshot(),
        );

        if (!field) {
          return <div></div>;
        }

        const context = state.context;
        const selectedEquipmentOption = context.selectedEquipmentOption;
        const searchText = context.searchText;
        const searchResults = context.searchResults;

        const handleEquipmentSelect = (
          equipmentId: string,
          option?: SelectedEquipmentOption,
        ) => {
          if (option) {
            stateMachineService.send({
              type: "SELECT_EQUIPMENT",
              equipmentId,
              equipmentOption: option,
            });
            handleChange(option.id);
          }
        };

        const handleEquipmentRemove = (equipmentId: string) => {
          stateMachineService.send({ type: "REMOVE_EQUIPMENT", equipmentId });
          handleChange(undefined);
        };

        const handleSearchChange = (text: string) => {
          stateMachineService.send({ type: "SEARCH_EQUIPMENT", text });
        };

        return (
          <VStack className="gap-2">
            <EquipmentSelection
              options={searchText ? searchResults : []}
              value={selectedEquipmentOption?.id ?? null}
              selectedEquipmentOption={selectedEquipmentOption}
              searchText={searchText}
              onSearchChange={handleSearchChange}
              onValueChange={handleEquipmentSelect}
              onRemoveEquipment={handleEquipmentRemove}
              disabled={isDisabled}
              readonly={readOnly || field.readonly}
              field={field}
              placeholder={placeholder}
            />
          </VStack>
        );
      },
      [field, readOnly, equipmentType],
    );
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={false} //内部で切り替えるので
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

function EquipmentSelection(props: {
  options: SelectedEquipmentOption[];
  value: string | null;
  selectedEquipmentOption: SelectedEquipmentOption | null;
  searchText: string;
  onSearchChange?: (text: string) => void;
  onValueChange: (value: string, option?: SelectedEquipmentOption) => void;
  onRemoveEquipment?: (equipmentId: string) => void;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  field: Attribute;
}) {
  // NOTE: 検索テキストの選択後にクリアするために保持
  const [searchText, setSearchText] = useState("");

  const handleSearchChange = useCallback(
    (text: string) => {
      props.onSearchChange?.(text);
      setSearchText(text);
    },
    [props],
  );

  const handleOnSelect = useCallback(
    (value: string) => {
      const option = props.options.find((opt) => opt.value === value);
      if (option && !option.disabled) {
        props.onValueChange(value, option);
        setSearchText("");
      }
    },
    [props],
  );

  const handleRemoveSelectedOption = useCallback(
    (value: string) => {
      props.onRemoveEquipment?.(value);
    },
    [props],
  );
  return (
    <>
      {props.readonly ? (
        <ReadOnlyField
          value={props.selectedEquipmentOption?.label}
          field={props.field}
        />
      ) : (
        <Command shouldFilter={false}>
          <div className="flex min-h-[80px] gap-2 rounded-md border border-b border-neutral-muted-border px-3 py-2 shadow-xs">
            <div className="flex flex-1 flex-wrap items-start gap-1 py-2">
              {props.selectedEquipmentOption ? (
                // <InviteComboboxSelectedEquipmentOptionChip
                //   key={props.selectedEquipmentOption.id}
                //   option={props.selectedEquipmentOption}
                //   onSelect={handleRemoveSelectedOption}
                //   disabled={props.disabled}
                // />
                <div className="flex items-center gap-1 rounded-md bg-neutral-fill px-2 py-1">
                  <span className="text-xs font-medium">
                    {props.selectedEquipmentOption.label}
                  </span>
                  {!props.disabled && (
                    <button
                      type="button"
                      onClick={() =>
                        props.selectedEquipmentOption &&
                        handleRemoveSelectedOption(
                          props.selectedEquipmentOption.value,
                        )
                      }
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ) : (
                <CommandInput
                  className="h-6 w-full text-xs outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={props.placeholder}
                  value={searchText}
                  onValueChange={handleSearchChange}
                  disabled={props.disabled}
                />
              )}
            </div>
          </div>
          {props.options.length ? (
            <CommandList
              className={cn(
                "gap-1 rounded-md border border-neutral-muted-border bg-neutral-fill p-1 shadow-xs",
              )}
            >
              {props.options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.value}
                  onSelect={
                    option.disabled
                      ? undefined
                      : () => handleOnSelect(option.value)
                  }
                  className={cn(
                    "cursor-pointer gap-1",
                    option.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{option.label}</span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          ) : (
            <></>
          )}
        </Command>
      )}
    </>
  );
}
